import { useParams, useSearchParams } from "react-router-dom";
import { isSpaceOrWorkspaceScope, useScope } from "~/hooks/useScope";
import {
  CaseStatus,
  GetCasesCountDocument,
  GetFindingOnAssetDocument,
  ScoreRating,
  useGetCasesCountQuery,
  useGetFindingOnAssetQuery,
  useGetRenderedAssetDataQueryQuery,
  useLoadAdvisoryQuery,
  useLoadAssetQuery,
  useLoadCveQuery,
} from "~/operations";
import { useFindingRiskFactors } from "~/pages/space/security/components/RiskFactors/hooks/useFindingRiskFactors";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { useTickets } from "~/pages/tickets/hooks";
import {
  getCheckFindingRemediation,
  getFindingAggregateScoreType,
  getFindingExceptionMrns,
  getFindingExceptionTarget,
  getFindingExceptionTypes,
  getFindingLinkBack,
  getFindingMrn,
  getFindingTitle,
  isAdvisoryFinding,
  isCheckFinding,
  isGenericFinding,
  isVulnerabilityFinding,
} from "../utils";
import { useFormattedAssetQuery } from "./useFormattedAssetQuery";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { useDialogs } from "@toolpad/core";
import { SetExceptionDialog } from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { CreateTicketDialogPayload } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketDialog";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { useSecurityPipelineIntegrations } from "~/pages/integrations/security-pipeline/hooks/useSecurityPipelineIntegrations";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";

export function useFindingOnAsset() {
  const { activeScope, spaceScope } = useScope();
  const [searchParams] = useSearchParams();
  const { id, vulnId = "", advisoryId = "" } = useParams();
  const checkMrn = searchParams.get("checkMrn") || "";
  const genericFindingMrn = searchParams.get("genericFindingMrn") || "";
  const jobId = searchParams.get("jobId") || "";
  const projectId = searchParams.get("projectId") || "";
  const dialogs = useDialogs();

  const ASSET_MRN = jobId
    ? jobId
    : `//assets.api.mondoo.app/spaces/${spaceScope?.id}/assets/${id}`;

  const findingMrn = getFindingMrn({
    vulnId,
    advisoryId,
    checkMrn,
    genericFindingMrn,
  });

  const scoreType = getFindingAggregateScoreType({
    vulnId,
    advisoryId,
    checkMrn,
    genericFindingMrn,
  });

  const exceptionTypes = getFindingExceptionTypes({
    vulnId,
    advisoryId,
    checkMrn,
    genericFindingMrn,
  });

  const {
    data: findingData,
    loading: loadingFindingOnAsset,
    refetch: refetchFindingOnAsset,
  } = useGetFindingOnAssetQuery({
    variables: {
      scopeMrn: ASSET_MRN,
      first: 1,
      filter: {
        mrn: findingMrn,
        includeCicd: Boolean(jobId),
      },
    },
    skip: !activeScope || !findingMrn,
  });

  const { data: cveData } = useLoadCveQuery({
    variables: { id: vulnId || "" },
    skip: !vulnId,
  });

  const { data: advisoryData } = useLoadAdvisoryQuery({
    variables: { id: advisoryId || "" },
    skip: !advisoryId,
  });

  const { riskFactorsWithDocs } = useFindingRiskFactors({
    spaceMrn: spaceScope?.mrn || "",
    findingMrn,
    ...(scoreType && { scoreType }),
    onlyAffected: false,
  });

  const { data: assetData } = useLoadAssetQuery({
    variables: {
      mrn: ASSET_MRN,
    },
    skip: !activeScope,
  });

  const {
    handleCreateCase,
    handleCreateTicketingIntegrationModalOpen,
    hasCreateTicketPermissions,
    hasIntegrationCreatePermission,
    hasListTicketsPermissions,
    loading: createTicketLoading,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
  });

  const handleCreateTicketDialogOpen = () => {
    if (!spaceScope || !isSpaceOrWorkspaceScope(activeScope)) return;

    const payload: CreateTicketDialogPayload = {
      isCombinedTicket: true,
      target: "asset",
      loading: createTicketLoading,
      onSave: (data) => handleCreateCase({ data }),
      space: spaceScope,
      scope: activeScope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      selectedEntities: [
        {
          mrn: finding?.mrn || "",
          scopeMrn: finding?.asset?.mrn || "",
        },
      ],
    };

    dialogs.open(CreateTicketDialog, payload);
  };

  const { hasTicketingIntegration } = useTicketingIntegrations();
  const { hasSecurityPipelineIntegrations } = useSecurityPipelineIntegrations();

  const findings =
    findingData?.findings?.__typename === "FindingsConnection"
      ? findingData.findings
      : undefined;

  const findingsNode = findings?.edges?.[0]?.node;

  const finding =
    isCheckFinding(findingsNode) ||
    isAdvisoryFinding(findingsNode) ||
    isVulnerabilityFinding(findingsNode) ||
    isGenericFinding(findingsNode)
      ? findingsNode
      : undefined;

  // get detection sources

  const getDetectionSources = () => {
    if (!isCheckFinding(finding) && !isGenericFinding(finding)) {
      return finding?.detectionSources;
    } else {
      return null;
    }
  };

  const detectionSources = getDetectionSources();

  /**
   * Get the latest exception from the exceptions array.
   * The last element in the array represents the most recent exception.
   * Returns undefined if exceptions is null, undefined, or empty.
   */
  const exception =
    finding?.exceptions && finding.exceptions.length > 0
      ? finding.exceptions[finding.exceptions.length - 1]
      : undefined;

  const { exceptionGroups, loadingListExceptionGroup, refetch } =
    useFetchExceptions({
      scopeMrn: ASSET_MRN,
      types: exceptionTypes,
      exceptionId: exception?.exceptionId,
      skip: exceptionTypes.length === 0 || !exception?.exceptionId,
    });

  const asset = assetData?.asset;

  //chop and sort the labels the way we want to display them
  const labels = finding?.asset?.platform?.labels || [];
  const labelsCopy = [...labels];
  // sort the labels so that any values that contain sha256 are at the end
  const mungedLabels = labelsCopy
    .sort((a, b) => {
      if (a.value?.substring(0, 6) === "sha256") {
        return 1;
      }
      if (b.value?.substring(0, 6) === "sha256") {
        return -1;
      }
      return 0;
    })
    // repackage the labels with an ellipse if they are sha256
    .map((label) => {
      const isLong = label.value && label.value.length > 20;
      const shouldEllipse = isLong;

      return {
        key: label.key,
        value: label.value,
        displayValue: shouldEllipse
          ? "..." + label.value?.substring(label.value.length - 7)
          : undefined,
      };
    });

  const isExceptionFromAssetScope = exception?.scope === ASSET_MRN;

  const {
    handleRemoveExceptionGroups,
    handleSetException,
    loading: exceptionsLoading,
  } = useExceptions({
    scopeMrns: [ASSET_MRN],
    ...getFindingExceptionMrns({
      vulnId,
      advisoryId,
      checkMrn,
      genericFindingMrn,
    }),
    refetchQueries: [GetFindingOnAssetDocument],
  });

  const { data: tickets } = useGetCasesCountQuery({
    variables: {
      input: {
        scopeMrn: asset?.mrn || "",
        findingMrns: [finding?.mrn || ""],
        statuses: [CaseStatus.Open],
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !asset?.mrn || !finding?.mrn || !hasListTicketsPermissions,
  });

  const { data: queryResultData } = useGetRenderedAssetDataQueryQuery({
    variables: {
      input: {
        assetMrn: ASSET_MRN,
        queryMrn: findingMrn,
      },
    },
    skip: !isCheckFinding(finding),
  });

  const queryResultsNotFoundErrorMessage =
    queryResultData?.renderedAssetQueryData?.__typename === "NotFoundError"
      ? queryResultData.renderedAssetQueryData.message
      : null;

  const queryResultsRenderedData =
    queryResultData?.renderedAssetQueryData?.__typename ===
    "RenderedAssetQueryData"
      ? queryResultData.renderedAssetQueryData
      : null;

  const { results: queryResults } = useFormattedAssetQuery(
    queryResultsRenderedData,
    finding?.state,
  );

  const linkToAsset = `/space/inventory/${asset?.id}/overview?spaceId=${activeScope?.id}`;
  const linkToException = isExceptionFromAssetScope
    ? `/space/inventory/${asset?.id}/exceptions?spaceId=${activeScope?.id}&exceptionId=${exception?.id}`
    : getExceptionLink({
        id: exception?.exceptionId || "",
        queryParams: spaceScope?.params || new URLSearchParams(),
      });

  const linkBack = getFindingLinkBack({
    vulnId,
    advisoryId,
    checkMrn,
    genericFindingMrn,
    jobId,
    projectId,
    assetId: asset?.id || "",
    scopeParams: activeScope?.params || new URLSearchParams(),
  });

  const ticketsCount = tickets?.cases.totalCount || 0;

  const checkData = isCheckFinding(finding) ? finding.mquery : undefined;

  const checkRemediation = getCheckFindingRemediation({
    finding,
    checkData,
  });

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      target: getFindingExceptionTarget(finding),
      onSave: handleSetException,
      selectedEntities: [
        {
          mrn: finding?.mrn || "",
          rating: finding?.rating || ScoreRating.Unscored,
          title: finding?.title || "",
        },
      ],
      targetData: {
        onAsset: finding?.asset || null,
        targetTitle: finding?.asset
          ? `${getFindingTitle({ finding })} on ${finding.asset.name}`
          : getFindingTitle({ finding }),
      },
    });
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: handleRemoveExceptionGroups,
      exceptionMrns: exceptionGroups.map((eg) => eg.mrn),
    });
  };

  const handleSecurityPipelinePRDialogOpen = () => {
    if (!spaceScope || !isSpaceOrWorkspaceScope(activeScope)) return;

    if (!isAdvisoryFinding(finding) && !isVulnerabilityFinding(finding)) return;

    dialogs.open(CreateSecurityPipelinePrDialog, {
      findings: [finding],
      spaceScope: spaceScope,
      assetMrns: [ASSET_MRN],
    });
  };

  return {
    asset,
    linkToAsset,
    linkToException,
    linkBack,
    finding,
    detectionSources,
    exception,
    riskFactorsWithDocs,
    cveData: cveData?.cve,
    advisoryData: advisoryData?.advisory,
    checkData: checkData,
    checkRemediation,
    labelTags: mungedLabels,
    activeScope,
    spaceScope,
    hasTicketingIntegration,
    hasSecurityPipelineIntegrations,
    searchParams,
    ticketsCount,
    queryResults,
    queryResultsNotFoundErrorMessage,
    isExceptionFromAssetScope,
    loadingFindingOnAsset,
    loadingListExceptionGroup,
    refetchFindingOnAsset,
    handleSecurityPipelinePRDialogOpen,
    exceptionActions: {
      handleSetExceptionModalOpen,
      handleRemoveExceptionModalOpen,
      loading: exceptionsLoading,
      exceptionGroups,
      exceptionTypes,
      refetch,
      openExceptions: {
        scopeMrn: spaceScope?.mrn || "",
        exceptionId: exception?.exceptionId,
        types: exceptionTypes,
      },
    },
    ticketActions: {
      handleCreateTicketDialogOpen,
      handleCreateTicketingIntegrationModalOpen,
      handleCreateCase,
      hasCreateTicketPermissions,
      hasIntegrationCreatePermission,
      hasListTicketsPermissions,
      loading: createTicketLoading,
    },
  };
}
